import { Component, Input, Output, EventEmitter, ContentChild, TemplateRef, signal, HostListener, OnInit, OnDestroy, ElementRef, inject, Renderer2, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { AppSelect, SelectOption } from '../app-select/app-select';
import { AppButton } from '../app-button/app-button';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';

export interface DataColumn {
   title: string;
   dataIndex?: string | string[];
   key: string;
   align?: 'left' | 'center' | 'right';
   width?: string | number;
   class?: string;
}

export interface PaginationState {
   current: number;
   limit: number;
   total: number;
}

@Component({
   selector: 'app-data-table',
   standalone: true,
   imports: [CommonModule, FormsModule, AppButton],
   schemas: [CUSTOM_ELEMENTS_SCHEMA],
   template: `
    <div class="space-y-4 animate-fade-in px-1">
      <!-- ── Header Section ────────────────────────────────────── -->
      <div class="flex flex-col gap-3 relative z-10">
        <!-- Title & Metrics Row -->
        <div *ngIf="title || description || metricsTemplate" class="flex flex-col gap-4 w-full border-b border-foreground/[0.03] pb-3 px-1">
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="flex items-center gap-3 min-w-0">
              <div *ngIf="icon" class="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0 transition-all border border-primary/20 shadow-sm">
                <iconify-icon [attr.icon]="icon" class="text-xl md:text-2xl text-blue-600 dark:text-blue-400"></iconify-icon>
              </div>
              <div class="flex flex-col justify-center gap-0.5 min-w-0">
                <h2 *ngIf="title" class="text-sm md:text-base font-bold text-foreground/90 dark:text-white leading-tight uppercase tracking-wide whitespace-nowrap overflow-hidden text-ellipsis">
                  {{ title }}
                </h2>
                <p *ngIf="description" class="text-[9px] md:text-[10px] text-foreground/75 font-bold leading-tight line-clamp-2 max-w-[200px] md:max-w-[400px]">
                  {{ description }}
                </p>
              </div>
            </div>

            <!-- Metrics Area (Optional Slot) -->
            <div class="flex items-center gap-2 flex-shrink-0">
              <ng-container *ngTemplateOutlet="metricsTemplate"></ng-container>
            </div>
          </div>

          <!-- Extra Header Area (e.g. for metrics under title) -->
          <div *ngIf="extraHeaderTemplate" class="animate-fade-in">
             <ng-container *ngTemplateOutlet="extraHeaderTemplate"></ng-container>
          </div>
        </div>

        <!-- Filters Row (Desktop: Inline, Mobile: Bottom Sheet/Modal) -->
        <ng-container *ngIf="filtersTemplate && showFilters()" class="z-1">
          <!-- Desktop Version -->
          <div *ngIf="!isMobile()" class="relative z-10 animate-fade-in transition-all duration-300">
             <ng-container *ngTemplateOutlet="filtersTemplate"></ng-container>
          </div>

          <!-- Mobile Version (Bottom Sheet) -->
          <div *ngIf="isMobile()" 
               class="fixed inset-0 z-[200] flex items-end justify-center animate-fade-in">
             <!-- Overlay -->
             <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" (click)="showFilters.set(false)"></div>
             
             <!-- Sheet Content -->
             <div class="relative w-full max-h-[85vh] bg-white dark:bg-slate-900 rounded-t-[2.5rem] p-6 shadow-2xl border-t border-foreground/5 overflow-y-auto animate-slide-up">
                <div class="flex items-center justify-between mb-8">
                   <div class="flex items-center gap-3">
                      <div class="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary border border-primary/20">
                         <iconify-icon icon="mdi:filter-variant" class="text-xl"></iconify-icon>
                      </div>
                      <h3 class="text-base font-black uppercase tracking-widest text-foreground">Filtros Avanzados</h3>
                   </div>
                   <button (click)="showFilters.set(false)" class="p-2 hover:bg-foreground/5 rounded-full transition-colors">
                      <iconify-icon icon="mdi:close" class="text-xl text-foreground/40"></iconify-icon>
                   </button>
                </div>
                
                <div class="space-y-6">
                   <ng-container *ngTemplateOutlet="filtersTemplate"></ng-container>
                </div>

                <div class="mt-10 pt-4 border-t border-foreground/5 flex flex-col gap-3 pb-4">
                   <app-button variant="primary" size="lg" (btnClick)="showFilters.set(false)" class="w-full">
                      Aplicar Filtros
                   </app-button>
                </div>
             </div>
          </div>
        </ng-container>

        <!-- Toolbar Row (Actions & Search) -->
        <div class="flex flex-col sm:flex-row items-center justify-between gap-4 relative z-0">
          <!-- Left: Actions & Reload -->
          <div class="flex items-center gap-2 w-full sm:w-auto">
            <ng-container *ngTemplateOutlet="actionGroupTemplate"></ng-container>
            
            <app-button 
               class="p-2"
                variant="glass" 
                size="md" 
                [square]="true"
                [spinIconOnHover]="true"
                (btnClick)="onReload.emit()" 
                title="Recargar tabla"
                [iconLeft]="'mdi:refresh'">
            </app-button>

            <!-- Toggle Filters -->
            <app-button *ngIf="filtersTemplate"
                class="p-2"
                [variant]="showFilters() ? 'primary' : 'glass'" 
                size="md" 
                [square]="true"
                (btnClick)="showFilters.set(!showFilters())" 
                [title]="showFilters() ? 'Ocultar Filtros' : 'Mostrar Filtros'"
                [iconLeft]="showFilters() ? 'mdi:filter-off' : 'mdi:filter-cog'">
            </app-button>
          </div>

          <!-- Right: Search Input -->
          <div class="relative w-full sm:w-64 group">
             <ng-container *ngIf="searchTemplate; else defaultSearch">
                <ng-container *ngTemplateOutlet="searchTemplate"></ng-container>
             </ng-container>
             <ng-template #defaultSearch>
                <input type="text" (input)="handleSearch($event)" [placeholder]="searchPlaceholder"
                 class="w-full pl-10 pr-4 py-2 bg-sidebar/50 border border-foreground/[0.08] rounded-xl text-sm font-medium text-foreground placeholder-foreground/60 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all shadow-sm" />
                <iconify-icon icon="mdi:magnify" 
                              class="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/60 text-lg transition-colors group-focus-within:text-primary"></iconify-icon>
             </ng-template>
          </div>
        </div>
      </div>

      <!-- ── Content Area ──────────────────────────────────────── -->
      <!-- Added fixed height to facilitate pagination presentation -->
      <div class="relative min-h-[45vh] flex flex-col bg-white dark:bg-slate-800 rounded-2xl border border-foreground/[0.05]">
        <!-- Loading Overlay -->
        <div *ngIf="loading" class="absolute inset-0 z-30 bg-surface/50 backdrop-blur-sm flex items-center justify-center rounded-2xl border border-foreground/5">
          <div class="flex flex-col items-center gap-3">
            <div class="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
            <span class="text-xs font-bold uppercase tracking-widest text-primary animate-pulse">Cargando...</span>
          </div>
        </div>

        <!-- Shared Empty State -->
        <ng-template #emptyState>
           <div class="py-16 min-h-[400px] flex flex-col items-center justify-center text-foreground/60 
           animate-fade-in flex-1">
              <div class="w-16 h-16 bg-sidebar rounded-full flex items-center justify-center mb-4 border border-foreground/[0.05]">
                  <iconify-icon icon="mdi:database-off-outline" class="text-2xl opacity-20"></iconify-icon>
              </div>
              <p class="font-medium">No se encontraron datos</p>
           </div>
        </ng-template>

        <!-- Mobile Card View -->
        <div *ngIf="isMobile()" class="grid grid-cols-1 sm:grid-cols-2 flex-1">
          <ng-container *ngIf="dataSource.length > 0; else emptyState">
             <div *ngFor="let item of dataSource; let i = index" 
                  class="p-4 hover:bg-primary/5 transition-all duration-300 group relative overflow-hidden">
                
                <!-- Decorative Gradient -->
                <div class="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>

                <!-- Card Header -->
                <div class="flex justify-between items-start mb-4 relative z-10">
                   <div class="flex-1 min-w-0 pr-4">
                      <span class="text-[10px] font-black uppercase tracking-widest text-foreground/20 block mb-1">
                        {{ columns[0].title }}
                      </span>
                      <h3 class="text-lg font-bold text-foreground truncate">
                        <ng-container *ngTemplateOutlet="columnTemplates[columns[0].key] || defaultCell; context: { $implicit: item, text: resolveDataIndex(item, columns[0].dataIndex) }"></ng-container>
                      </h3>
                      <div *ngIf="columns[1]" class="text-sm text-foreground/75 truncate mt-0.5">
                        <ng-container *ngTemplateOutlet="columnTemplates[columns[1].key] || defaultCell; context: { $implicit: item, text: resolveDataIndex(item, columns[1].dataIndex) }"></ng-container>
                      </div>
                   </div>
                   <div class="flex gap-2">
                      <ng-container *ngTemplateOutlet="actionsTemplate; context: { $implicit: item }"></ng-container>
                   </div>
                </div>

                <!-- Card Body -->
                <div class="space-y-3 relative z-10">
                   <div *ngFor="let col of columns.slice(2)" class="flex flex-col">
                      <span class="text-[10px] font-bold uppercase tracking-wider text-foreground/60">
                        {{ col.title }}
                      </span>
                      <div class="text-sm font-medium text-foreground/80 mt-0.5 break-words">
                        <ng-container *ngTemplateOutlet="columnTemplates[col.key] || defaultCell; context: { $implicit: item, text: resolveDataIndex(item, col.dataIndex) }"></ng-container>
                      </div>
                   </div>
                </div>
             </div>
          </ng-container>
        </div>

        <!-- Desktop Table View -->
        <div *ngIf="!isMobile()" class="bg-surface/40 backdrop-blur-xl border border-foreground/[0.05] overflow-hidden flex-1 flex flex-col">
          <div class="overflow-x-auto flex-1">
            <table class="w-full text-left border-collapse">
               <thead>
                  <tr class="border-b border-foreground/[0.05] bg-foreground/[0.02]">
                     <th *ngIf="expandable" class="px-6 py-3 w-12"></th>
                     <th *ngFor="let col of columns" 
                         class="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-foreground/75 whitespace-nowrap"
                         [class]="[col.class || '', col.align ? 'text-' + col.align : '']"
                         [style.width]="col.width">
                       {{ col.title }}
                     </th>
                  </tr>
               </thead>
               <tbody class="divide-y divide-foreground/[0.03]">
                  <ng-container *ngFor="let item of dataSource; let i = index">
                     <tr class="hover:bg-primary/[0.03] transition-colors duration-200 group">
                        <td *ngIf="expandable" class="px-4 py-1 w-12">
                           <button (click)="toggleExpand(i)" class="w-7 h-7 rounded-lg hover:bg-primary/10 flex items-center justify-center transition-all group">
                              <iconify-icon icon="mdi:chevron-right" width="16" 
                                            class="text-foreground/20 transition-transform duration-200"
                                            [class.rotate-90]="isExpanded(i)"></iconify-icon>
                           </button>
                        </td>

                        <td *ngFor="let col of columns" class="px-6 py-1.5 text-sm font-medium text-foreground/70"
                            [class]="[col.class || '', col.align ? 'text-' + col.align : '']"
                            [style.width]="col.width">
                           <ng-container *ngTemplateOutlet="columnTemplates[col.key] || defaultCell; context: { $implicit: item, text: resolveDataIndex(item, col.dataIndex) }"></ng-container>
                           <div *ngIf="col.key === 'actions' || col.key === 'action'">
                              <ng-container *ngTemplateOutlet="actionsTemplate; context: { $implicit: item }"></ng-container>
                           </div>
                        </td>
                     </tr>
                     
                     <tr *ngIf="expandable && isExpanded(i)" class="bg-surface/30">
                        <td [attr.colspan]="columns.length + 1" class="px-4 py-4 animate-fade-in">
                           <ng-container *ngTemplateOutlet="expandedRowTemplate; context: { $implicit: item, index: i }"></ng-container>
                        </td>
                     </tr>
                  </ng-container>
               </tbody>
            </table>
          </div>

          <ng-container *ngIf="dataSource.length === 0">
             <ng-container *ngTemplateOutlet="emptyState"></ng-container>
          </ng-container>
        </div>

         <!-- ── Pagination & Limit Section (Premium Refinement) ────────────────────────── -->
         <!-- ── Pagination & Limit Section (Compact Refinement) ────────────────────────── -->
      <div *ngIf="pagination" class="flex items-center justify-between gap-3 px-4 py-1 bg-surface dark:bg-slate-800 border-t border-foreground/[0.03] rounded-b-2xl text-[11px] font-semibold text-foreground/50">
         <!-- Meta Info -->
         <div class="flex items-center gap-1.5">
            <span>Viendo <span class="text-foreground font-bold">{{ Math.min((pagination.current - 1) * pagination.limit + 1, pagination.total) }}</span> - <span class="text-foreground font-bold">{{ Math.min(pagination.current * pagination.limit, pagination.total) }}</span></span>
            <span class="w-1 h-1 rounded-full bg-foreground/10 mx-0.5"></span>
            <span>Total <span class="text-primary font-black ml-0.5">{{ pagination.total }}</span></span>
         </div>

         <!-- Controls -->
         <div class="flex items-center gap-3">
            <!-- Limit Selection custom compact -->
            <div class="flex items-center gap-1">
               <div class="relative" #limitContainer>
                  <button (click)="limitMenu.set(!limitMenu())" class="flex items-center gap-1 bg-foreground/[0.03] dark:bg-white/[0.02] px-1.5 py-0.5 rounded-lg border border-foreground/[0.03] text-[10px] font-bold text-foreground hover:bg-foreground/[0.05] transition-colors">
                     {{ pagination.limit }}
                     <iconify-icon icon="mdi:chevron-down" class="text-foreground/60 text-[9px]"></iconify-icon>
                  </button>
                  
                  <div *ngIf="limitMenu()" class="absolute bottom-full right-0 mb-1 w-14 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-foreground/[0.05] z-50 overflow-hidden animate-fade-in">
                     <button *ngFor="let opt of limitOptions" (click)="onLimitChange.emit(+opt.value); limitMenu.set(false)"
                        class="w-full text-center px-1 py-1.5 text-[10px] font-bold text-foreground/90 dark:text-white hover:bg-primary/10 transition-colors"
                        [class.bg-primary/5]="+opt.value === pagination.limit"
                        [class.text-primary]="+opt.value === pagination.limit">
                        {{ opt.label }}
                     </button>
                  </div>
               </div>
               <span class="text-[9px] font-black uppercase opacity-60 whitespace-nowrap pr-0.5">Filas</span>
            </div>

            <!-- Page Buttons -->
            <div class="flex items-center gap-0.5 p-0.5 bg-foreground/[0.02] dark:bg-white/[0.02] rounded-lg border border-foreground/[0.03]">
               <app-button 
                   variant="glass" 
                   class="p-0 flex items-center justify-center min-w-[24px!important] h-[24px!important]"
                   (btnClick)="handlePageChange(pagination.current - 1)" 
                   [disabled]="pagination.current <= 1"
                   iconLeft="mdi:chevron-left">
               </app-button>

               <div class="flex items-center gap-0.5">
                  <ng-container *ngFor="let p of getPageRange()">
                     <app-button 
                       *ngIf="p > 0"
                       [variant]="p === pagination.current ? 'primary' : 'glass'"
                       class="p-0 flex items-center justify-center min-w-[24px!important] h-[24px!important] text-[10px] font-bold"
                       (btnClick)="handlePageChange(p)"
                       [class.shadow-sm]="p === pagination.current">
                       {{ p }}
                     </app-button>
                     <span *ngIf="p === -1" class="w-3 text-center text-foreground/20 text-[10px] font-black tracking-widest">...</span>
                  </ng-container>
               </div>

               <app-button 
                   variant="glass" 
                   class="p-0 flex items-center justify-center min-w-[24px!important] h-[24px!important]"
                   (btnClick)="handlePageChange(pagination.current + 1)" 
                   [disabled]="pagination.current >= totalPages"
                   iconLeft="mdi:chevron-right">
               </app-button>
            </div>
         </div>
      </div>
      </div>

     
    </div>

    <!-- Default Cell -->
    <ng-template #defaultCell let-text="text">{{ text }}</ng-template>
  `,
   styles: [`
    :host { display: block; }
    select option { 
        background-color: var(--surface);
        color: var(--foreground);
        font-weight: 700;
    }
  `]
})
export class AppDataTable implements OnInit, OnDestroy {
   private el = inject(ElementRef);
   private renderer = inject(Renderer2);
   @ViewChild('limitContainer') limitContainer?: ElementRef;
   private clickListener?: () => void;
   @Input() title?: string;
   @Input() description?: string;
   @Input() icon?: string;
   @Input() dataSource: any[] = [];
   @Input() columns: DataColumn[] = [];
   @Input() loading = false;
   @Input() total = 0;
   @Input() expandable = false;
   @Input() searchPlaceholder = 'Buscar...';
   @Input() hasSummary = false;
   @Input() pagination?: PaginationState;

   @Input() columnTemplates: { [key: string]: TemplateRef<any> } = {};

   @ContentChild('actionGroup') actionGroupTemplate?: TemplateRef<any>;
   @ContentChild('metrics') metricsTemplate?: TemplateRef<any>;
   @ContentChild('extraHeader') extraHeaderTemplate?: TemplateRef<any>;
   @ContentChild('search') searchTemplate?: TemplateRef<any>;
   @ContentChild('filters') filtersTemplate?: TemplateRef<any>;
   @ContentChild('actions') actionsTemplate?: TemplateRef<any>; // New slot for cell actions
   @ContentChild('expandedRow') expandedRowTemplate?: TemplateRef<any>;
   @ContentChild('summary') summaryTemplate?: TemplateRef<any>;

   @Output() onReload = new EventEmitter<void>();
   @Output() onSearch = new EventEmitter<string>();
   @Output() onPageChange = new EventEmitter<number>();
   @Output() onLimitChange = new EventEmitter<number>();

   expandedRows = new Set<number>();
   Math = Math;
   limitMenu = signal(false);
   showFilters = signal(false);

   private searchSubject = new Subject<string>();
   private destroy$ = new Subject<void>();

   limitOptions: SelectOption[] = [
      { label: 10, value: 10 },
      { label: 20, value: 20 },
      { label: 50, value: 50 },
      { label: 100, value: 100 }
   ];

   isMobile = signal(window.innerWidth < 1024);

   @HostListener('window:resize')
   onResize() {
      this.isMobile.set(window.innerWidth < 1024);
   }


   ngOnInit() {
      this.clickListener = this.renderer.listen('document', 'click', (event: MouseEvent) => {
         if (this.limitMenu() && this.limitContainer && !this.limitContainer.nativeElement.contains(event.target)) {
            this.limitMenu.set(false);
         }
      }, { capture: true });

      this.searchSubject.pipe(
         debounceTime(400),
         distinctUntilChanged(),
         takeUntil(this.destroy$)
      ).subscribe(value => {
         this.onSearch.emit(value);
      });
   }

   ngOnDestroy() {
      this.searchSubject.complete();
      this.destroy$.next();
      this.destroy$.complete();
      if (this.clickListener) this.clickListener();
   }

   get totalPages(): number {
      if (!this.pagination) return 0;
      return Math.ceil(this.pagination.total / this.pagination.limit);
   }

   toggleExpand(index: number) {
      if (this.expandedRows.has(index)) {
         this.expandedRows.delete(index);
      } else {
         this.expandedRows.add(index);
      }
   }

   isExpanded(index: number): boolean {
      return this.expandedRows.has(index);
   }

   resolveDataIndex(item: any, dataIndex?: string | string[]): any {
      if (!dataIndex) return '';
      if (Array.isArray(dataIndex)) {
         return dataIndex.reduce((acc, key) => (acc && acc[key] !== undefined) ? acc[key] : '', item);
      }
      return item[dataIndex];
   }

   handleSearch(event: any) {
      this.searchSubject.next(event.target.value);
   }

   handlePageChange(page: number) {
      if (this.pagination && page >= 1 && page <= this.totalPages) {
         this.onPageChange.emit(page);
      }
   }

   handleLimitChange(limit: number) {
      this.onLimitChange.emit(limit);
   }

   getPageRange(): number[] {
      if (!this.pagination) return [];
      const current = this.pagination.current;
      const total = this.totalPages;
      const range: number[] = [];

      for (let i = 1; i <= total; i++) {
         if (i === 1 || i === total || (i >= current - 1 && i <= current + 1)) {
            range.push(i);
         } else if (i === current - 2 || i === current + 2) {
            range.push(-1); // Ellipsis
         }
      }
      // Remove consecutive Ellipsis
      return range.filter((v, i, a) => v !== -1 || a[i - 1] !== -1);
   }
}
